/* eslint-disable max-statements */

import { Group, LoadingOverlay } from '@mantine/core';
import { cloneDeep } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { BatchTypeEnum, Declaration, DeclarationHighValueGroupType, DeclarationStatus, IDeclarationGroupsCriteria } from '@e-origin/shared';

import { BackButton, ListFilterChangeEvent, ListFilterDropdown, ListFilterDropdownElements, SearchBox, Table } from '@components';
import { BatchView } from '@features/batches';
import { ContentLayout } from '@features/layout';
import { useBatch } from '@hooks/batch';
import { getInitialCriteria, useDeclarationGroups, useDeclarationGroupsMeta } from '@hooks/declarationGroup';
import { useSettings } from '@hooks/useSettings';
import { removeUndefined, STORAGE_KEYS } from '@utils';
import { getStoredCriteria, setStoredCriteria } from '../../../../criteria.caching';
import { useDeclarationGroupTableColumns } from './DeclarationsGroupTable';

export const DeclarationGroupList = () => {
  const routeLocation = useLocation();
  const queryParams = useRef<URLSearchParams>(null);
  const batchUrlParam = queryParams?.current?.get('batchId') ?? undefined;
  const { data: settings } = useSettings();

  const criteria = getInitialCriteria();

  const [filters, setFilters] = useState<IDeclarationGroupsCriteria['filters']>(criteria.filters);
  const [sorting, setSorting] = useState<IDeclarationGroupsCriteria['sorting']>(criteria.sorting);
  const [pagination, setPagination] = useState<IDeclarationGroupsCriteria['pagination']>(criteria.pagination);

  const { data: declarationGroups = [], isLoading } = useDeclarationGroups({ filters, sorting, pagination });

  const { data: { total: totalDeclarationGroups } = {} } = useDeclarationGroupsMeta({ filters });

  const criteriaInitialized = useRef<boolean>(false);

  const { data: selectedBatch } = useBatch(filters.batchId);
  const [batchView, setBatchView] = useState((getStoredCriteria(STORAGE_KEYS.COOKIES.BATCH_VIEW) as BatchTypeEnum) || BatchTypeEnum.HIGH_VALUES_EXPORT);

  const [groupOptions, setGroupOptions] = useState<ListFilterDropdownElements>(() => {
    return Object.values(DeclarationHighValueGroupType).reduce((acc, type) => {
      acc[type] = {
        label: type,
        checked: criteria.filters.groupTypes?.includes(type) ?? false,
      };
      return acc;
    }, {} as ListFilterDropdownElements);
  });

  const selectedRow = useRef<Declaration>(null);

  useEffect(() => {
    queryParams.current = new URLSearchParams(routeLocation.search);
  }, [routeLocation]);

  const onSelectedRow = (row) => {
    selectedRow.current = row;
  };

  const columns = useDeclarationGroupTableColumns({ onSelectedRow });

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination({ page: 1, size: pagination.size });
  };

  const handlePagination = (page: number, size: number) => {
    const updatedPagination: IDeclarationGroupsCriteria['pagination'] = { page, size };
    if (![...queryParams.current].length) {
      setStoredCriteria(STORAGE_KEYS.COOKIES.LIST_DECLARATION_GROUPS_CRITERIA, { filters, sorting, pagination: updatedPagination });
    }
    const firstGroup: any = declarationGroups[0];
    if (firstGroup?.paginationToken) {
      updatedPagination.searchToken = firstGroup.paginationToken;
    }
    setPagination(updatedPagination);
  };

  const setDeclarationGroupsFilters = (
    updatedFilters: Partial<IDeclarationGroupsCriteria['filters']>,
    options: { replaceCriteria: boolean } = { replaceCriteria: false },
  ) => {
    setFilters(
      options.replaceCriteria
        ? updatedFilters
        : removeUndefined({
            ...filters,
            ...updatedFilters,
          }),
    );
  };

  const buildStatusesDropDownOptions = () => {
    let dropdownOptions: ListFilterDropdownElements = {
      [DeclarationStatus.ANALYSIS as string]: {
        label: 'Risk analysis',
        checked: false,
      },
      ...(!settings?.isForwarder
        ? {
            [DeclarationStatus.BEGATE_SENT as string]: {
              label: 'BE-Gate Sent',
              checked: false,
            },
            [DeclarationStatus.BEGATE_NOT_RELEASED as string]: {
              label: 'BE-Gate Not Released',
              checked: false,
            },
            [DeclarationStatus.BEGATE_RELEASED as string]: {
              label: 'BE-Gate Released',
              checked: false,
            },
          }
        : {}),
      [DeclarationStatus.NOT_SENT as string]: {
        label: 'Not sent',
        checked: false,
      },
      [DeclarationStatus.SENDING as string]: {
        label: 'Sending',
        checked: false,
      },
      [DeclarationStatus.SENT as string]: {
        label: 'Sent',
        checked: false,
      },
      [DeclarationStatus.ACCEPTED as string]: {
        label: 'Validation in progress',
        checked: false,
      },
      [DeclarationStatus.REJECTED as string]: {
        label: 'Rejected',
        checked: false,
      },
      [DeclarationStatus.IN_CONTROL as string]: {
        label: 'In control',
        checked: false,
      },
      [DeclarationStatus.NOT_RELEASED as string]: {
        label: 'Not released',
        checked: false,
      },
      [DeclarationStatus.RELEASED as string]: {
        label: 'Released',
        checked: false,
      },
      [DeclarationStatus.INVALIDATED as string]: {
        label: 'Invalidated',
        checked: false,
      },
    };

    if (batchView === BatchTypeEnum.HIGH_VALUES_EXPORT) {
      dropdownOptions = {
        ...dropdownOptions,
        [DeclarationStatus.EXPORTED as string]: {
          label: 'Exported',
          checked: false,
        },
      };
    }

    if (![...queryParams.current].length) {
      filters.statuses?.forEach((key) => {
        if (dropdownOptions[key]) {
          dropdownOptions[key].checked = true;
        }
      });
    }

    return dropdownOptions;
  };

  const [statusesOptions, setStatusesOptions] = useState<ListFilterDropdownElements>(null);

  useEffect(() => {
    if (!criteriaInitialized.current) {
      return;
    }

    if (![...queryParams.current].length) {
      setStoredCriteria(STORAGE_KEYS.COOKIES.LIST_DECLARATION_GROUPS_CRITERIA, { filters, sorting, pagination });
    }
  }, [filters, sorting]);

  useEffect(() => {
    if (!routeLocation) {
      return;
    }

    queryParams.current = new URLSearchParams(routeLocation.search);
    const newDropdownOptions = buildStatusesDropDownOptions();
    const validStatuses = filters.statuses?.filter((status) => newDropdownOptions[status]) || [];

    const batchId = queryParams.current.get('batchId') ?? undefined;

    if ([...queryParams.current].length) {
      if (filters?.batchId !== batchId && batchId) {
        setPagination({ page: 1, size: pagination.size });
      }

      setDeclarationGroupsFilters(
        {
          batchId,
          isExport: batchView === BatchTypeEnum.HIGH_VALUES_EXPORT,
          ...(batchView === BatchTypeEnum.HIGH_VALUES_EXPORT && { groupTypes: [] }),
        },
        { replaceCriteria: true },
      );
    } else {
      setDeclarationGroupsFilters({
        isExport: batchView === BatchTypeEnum.HIGH_VALUES_EXPORT,
        statuses: validStatuses,
        ...(batchView === BatchTypeEnum.HIGH_VALUES_EXPORT && { groupTypes: [] }),
      });
    }

    setStatusesOptions(newDropdownOptions);

    if (batchView === BatchTypeEnum.HIGH_VALUES_EXPORT) {
      setGroupOptions(
        Object.values(DeclarationHighValueGroupType).reduce((acc, type) => {
          acc[type] = { label: type, checked: false };
          return acc;
        }, {} as ListFilterDropdownElements),
      );
    }
    criteriaInitialized.current = true;
  }, [routeLocation, batchView]);

  const handleBatchView = (value: BatchTypeEnum) => {
    setBatchView(value);
  };

  const searchDeclarations = (searchQuery: string) => {
    setDeclarationGroupsFilters({
      searchQuery,
      isExport: batchView === BatchTypeEnum.HIGH_VALUES_EXPORT,
    });
    setPagination({ ...pagination, page: 1 });
  };

  const filterByStatus = (statusOption: ListFilterChangeEvent) => {
    let selectedStatuses = [];
    const statusesOptionsCopy = cloneDeep(statusesOptions);
    if (!statusOption.id) {
      Object.keys(statusesOptionsCopy).forEach((option) => {
        statusesOptionsCopy[option].checked = statusOption.checked;
      });
    } else {
      statusesOptionsCopy[statusOption.id].checked = statusOption.checked;
    }

    selectedStatuses = Object.keys(statusesOptionsCopy)
      .filter((option) => statusesOptionsCopy[option].checked)
      .map((option) => option);

    setStatusesOptions(statusesOptionsCopy);

    setDeclarationGroupsFilters({
      statuses: statusOption.id || selectedStatuses.length === Object.keys(statusesOptions).length ? selectedStatuses : [],
    });
  };

  const filterByGroup = (groupOption: ListFilterChangeEvent) => {
    let selectedGroups = [];
    const groupOptionsCopy = cloneDeep(groupOptions);
    if (!groupOption.id) {
      Object.keys(groupOptionsCopy).forEach((option) => {
        groupOptionsCopy[option].checked = groupOption.checked;
      });
    } else {
      groupOptionsCopy[groupOption.id].checked = groupOption.checked;
    }

    selectedGroups = Object.keys(groupOptionsCopy)
      .filter((option) => groupOptionsCopy[option].checked)
      .map((option) => option);

    setGroupOptions(groupOptionsCopy);

    setDeclarationGroupsFilters({
      groupTypes: groupOption.id || selectedGroups.length === Object.keys(groupOptions).length ? selectedGroups : [],
    });
  };

  if (!declarationGroups) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <ContentLayout>
      {batchUrlParam && (
        <Group ml={24}>
          <BackButton backPath={-1}></BackButton>
        </Group>
      )}
      <ContentLayout.Header>
        <ContentLayout.Title>Declarations {selectedBatch && <span>(Batch: {selectedBatch.name})</span>}</ContentLayout.Title>
        <Group>
          <BatchView value={batchView} onChange={handleBatchView} excludeList={[BatchTypeEnum.LOW_VALUES]} />
          {batchView !== BatchTypeEnum.LOW_VALUES && (
            <SearchBox handleSearch={searchDeclarations} value={filters?.searchQuery} placeholder="Search by LRN, MRN, batch name,..." />
          )}
        </Group>
      </ContentLayout.Header>
      {batchView !== BatchTypeEnum.LOW_VALUES && (
        <>
          <ContentLayout.SubHeader>
            <Group justify="flex-end" px="md" py="md">
              {batchView !== BatchTypeEnum.HIGH_VALUES_EXPORT && <ListFilterDropdown title={'Group filter'} values={groupOptions} onChange={filterByGroup} />}
              <ListFilterDropdown title={'Status filter'} values={statusesOptions} onChange={filterByStatus} />
            </Group>
          </ContentLayout.SubHeader>
          <ContentLayout.Content>
            <Table
              columns={columns}
              dataSource={declarationGroups}
              total={totalDeclarationGroups}
              onPaginationChange={handlePagination}
              onSortingChange={handleSorting}
              isLoading={isLoading}
              defaultPage={pagination.page}
              defaultPerPage={pagination.size}
              defaultSorting={sorting.field}
              defaultSortingDirection={sorting.direction}
            />
          </ContentLayout.Content>
        </>
      )}
    </ContentLayout>
  );
};
